import { HELP_VIDEO_IDS, type HelpVideoId } from '@tarrot/shared'

/**
 * Help-center video registry — batch 5 of the July 2026 design handoff. Nine
 * short screen.studio clips, one task per clip, free for every tier.
 *
 * Ported verbatim from the design prototype's `CF_HELP_VIDEOS` const
 * (help.jsx): ids, groups, titles, subs, durations, and tones are the source
 * of truth. `id` doubles as the deep-link anchor a contextual hint dot keys
 * into, and as the filename stem for the video + poster assets.
 *
 * ⚠️ The bare id list is mirrored in `@tarrot/shared` (HELP_VIDEO_IDS) so the
 * API can validate help analytics events without importing this web module. A
 * drift test (help.shared.test.ts) pins the two together.
 *
 * `available` is false for every entry — the clips are not filmed yet. Tasks
 * 2–3 render the "coming soon" affordance off this flag; flip an entry to true
 * once its `<id>-v1.mp4` / `<id>-poster.jpg` assets land under HELP_VIDEO_BASE.
 */

/** Display groups, in render order (matches CF_HELP_GROUPS). */
export const HELP_GROUPS = [
  'Getting started',
  'Your practice',
  'Cosmic features',
] as const

export type HelpGroup = (typeof HELP_GROUPS)[number]

/** Thumbnail accent tone — keys the tone→CSS-var map in the thumb/player UI. */
export type HelpTone = 'gold' | 'violet' | 'rose' | 'ghost'

export interface HelpVideo {
  /** Stable id: deep-link anchor + asset filename stem. */
  id: HelpVideoId
  /** Display group (one of HELP_GROUPS). */
  group: HelpGroup
  /** Card / player title. */
  title: string
  /** One-line sub-copy. */
  sub: string
  /** Target duration label from the recording guide, e.g. "0:45". */
  dur: string
  /** Thumbnail accent tone. */
  tone: HelpTone
  /** Whether the clip is filmed + published. False until assets land. */
  available: boolean
}

/** Base path for help-center media assets (served from public/). */
export const HELP_VIDEO_BASE = '/help-videos/'

/** Video source URL for a help clip: `<base><id>-v1.mp4`. */
export function videoSrc(id: HelpVideoId): string {
  return `${HELP_VIDEO_BASE}${id}-v1.mp4`
}

/** Poster (still) URL for a help clip: `<base><id>-poster.jpg`. */
export function posterSrc(id: HelpVideoId): string {
  return `${HELP_VIDEO_BASE}${id}-poster.jpg`
}

/** The registry, in the design-handoff order (grouped). */
export const HELP_VIDEOS: readonly HelpVideo[] = [
  // Getting started
  { id: 'first-pull', group: 'Getting started', title: 'Pull your first card', sub: 'Ask, shuffle, read the interpretation', dur: '0:45', tone: 'gold', available: false },
  { id: 'birth-data', group: 'Getting started', title: 'Add your birth data', sub: 'What unlocks with your chart', dur: '0:40', tone: 'violet', available: false },
  { id: 'moon-basics', group: 'Getting started', title: 'Read the moon calendar', sub: 'Phases, signs, and what today means', dur: '0:50', tone: 'violet', available: false },
  // Your practice
  { id: 'journal', group: 'Your practice', title: 'Save a reflection', sub: 'Journal after a reading', dur: '0:35', tone: 'gold', available: false },
  { id: 'ceremonies', group: 'Your practice', title: 'Join a live ceremony', sub: 'RSVP, reminders, joining the room', dur: '0:55', tone: 'rose', available: false },
  { id: 'sharing', group: 'Your practice', title: 'Share a reading', sub: 'Send a card to a friend — privately', dur: '0:40', tone: 'gold', available: false },
  // Cosmic features
  { id: 'decks', group: 'Cosmic features', title: 'Explore the deck library', sub: 'Switch decks, meet the practitioners', dur: '0:50', tone: 'violet', available: false },
  { id: 'themes', group: 'Cosmic features', title: 'Pick your theme', sub: 'Presets and your own accent color', dur: '0:45', tone: 'gold', available: false },
  { id: 'billing', group: 'Cosmic features', title: 'Manage your membership', sub: 'Upgrade, billing, cancel anytime', dur: '0:35', tone: 'ghost', available: false },
]

/** All registry ids, in order (mirrors HELP_VIDEO_IDS; the drift test locks it). */
export const HELP_VIDEO_IDS_WEB = HELP_VIDEOS.map((v) => v.id)

// Compile-time nudge: the shared id list and the web registry describe the same
// nine clips. The runtime drift test is the real guard.
void (HELP_VIDEO_IDS as readonly HelpVideoId[])
